import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Form, Button, Alert, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import logoImg from '../assets/ecoempower-logo.png'
import bgImg from '../assets/login-background.png'

export default function LoginPage() {
  const { login, user, loading: authLoading } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const from = location.state?.from?.pathname || '/'

  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true })
    }
  }, [user, authLoading, navigate, from])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await login(username, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err.message || t('login.invalid_credentials'))
    } finally {
      setSubmitting(false)
    }
  }

  if (authLoading) {
    return (
      <div style={styles.page}>
        <Spinner animation="border" variant="secondary" />
      </div>
    )
  }

  return (
    <div style={styles.page}>
      {/* Left side: login form */}
      <div style={styles.leftPanel}>
        <div style={styles.formWrapper}>
          {/* Logo */}
          <div style={styles.logoArea}>
            <img src={logoImg} alt="ECOEMPOWER" style={styles.logo} />
            <div style={styles.platformLabel}>ICT Platform</div>
          </div>

          {/* Login form */}
          <div style={styles.card}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label style={styles.label}>{t('login.username')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('login.username_placeholder')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={submitting}
                  autoFocus
                  required
                  style={styles.input}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={styles.label}>{t('login.password')}</Form.Label>
                <Form.Control
                  type="password"
                  placeholder={t('login.password_placeholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  required
                  style={styles.input}
                />
              </Form.Group>

              <Button
                type="submit"
                disabled={submitting || !username || !password}
                style={{
                  ...styles.loginButton,
                  opacity: (submitting || !username || !password) ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t('login.signing_in')}
                  </>
                ) : (
                  t('login.sign_in')
                )}
              </Button>
            </Form>

            {error && (
              <Alert variant="danger" className="mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                {error}
              </Alert>
            )}
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <div style={styles.footerDisclaimer}>
              {t('login.footer_disclaimer')}
            </div>
            <div style={styles.footerPowered}>
              {t('login.footer_powered_by')}{' '}
              <a
                href="https://energy.ubitech.eu/"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.footerLink}
              >
                UBITECH Energy
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right side: background image */}
      <div style={styles.rightPanel}>
        <img src={bgImg} alt="" style={styles.bgImage} />
      </div>
    </div>
  )
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'row',
  },
  leftPanel: {
    flex: '0 0 50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FAFAFA',
    padding: '40px 24px',
  },
  formWrapper: {
    width: '100%',
    maxWidth: '400px',
  },
  rightPanel: {
    flex: '0 0 50%',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
  },
  bgImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  logoArea: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    height: '48px',
    width: 'auto',
  },
  platformLabel: {
    fontSize: '0.85rem',
    color: '#999',
    marginTop: '6px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
    padding: '32px',
  },
  label: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#555',
  },
  input: {
    borderRadius: '8px',
    padding: '10px 14px',
  },
  loginButton: {
    width: '100%',
    backgroundColor: '#8CB808',
    borderColor: '#8CB808',
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    borderRadius: '8px',
    padding: '10px',
    transition: 'background-color 0.2s',
  },
  footer: {
    textAlign: 'center',
    marginTop: '28px',
    fontSize: '0.7rem',
    color: '#aaa',
    lineHeight: 1.5,
  },
  footerDisclaimer: {
    marginBottom: '8px',
  },
  footerPowered: {
    fontSize: '0.75rem',
    color: '#999',
  },
  footerLink: {
    color: '#8CB808',
    textDecoration: 'none',
    fontWeight: 600,
  },
}
